/** QuickPlan — Export STEP AP214 (ISO 10303-21, schéma AUTOMOTIVE_DESIGN).
 *
 * Solides B-rep EXACTS, écrits sans noyau CAO : QuickPlan ne connaît que trois
 * primitives, toutes exprimables en faces planes et cylindriques —
 *  - pavé et extrusion de section (profilés, tubes, cornières, panneaux) : prisme à
 *    faces planes, alésages compris (les sections sont des polygones) ;
 *  - cylindre : deux disques plans + deux demi-faces cylindriques (pas d'arête de
 *    couture, que certains lecteurs digèrent mal).
 *
 * Structure produit (lue par FreeCAD, SolidWorks, Fusion, Inventor, CATIA…) :
 *   produit racine (projet) → sous-ensembles QuickPlan (imbriqués) → pièces placées.
 * Une pièce = un produit partagé par toutes les instances identiques (même composant,
 * mêmes paramètres, même couleur) : 40 profilés de même longueur = 1 définition et
 * 40 occurrences. Les attaches parent/enfant sont aplaties (placement monde), les
 * articulations exportées dans leur état affiché. Unités : mm, Z vers le haut (repère
 * de QuickPlan, qui est celui de la CAO). Couleurs : un style par solide.
 */
import { resolveComponent, rotMatrix, apply, add, extrudeMap } from './resolve.js';
import { resolveSection, polygonArea } from './sections.js';
import { computeFrames } from './frames.js';

const EPS = 1e-6;

/** Réel au format STEP (point décimal obligatoire, exposant en E). */
export function stepReal(v) {
  if (!Number.isFinite(v)) throw new Error(`valeur non finie : ${v}`);
  const r = Math.abs(v) < 1e-12 ? 0 : v;
  if (Number.isInteger(r) && Math.abs(r) < 1e15) return `${r}.`;
  let s = String(Number(r.toPrecision(15)));
  if (/e/i.test(s)) {
    const [m, e] = s.split(/e/i);
    s = `${m.includes('.') ? m : `${m}.`}E${e}`;
  } else if (!s.includes('.')) s += '.';
  return s;
}

/** Chaîne STEP : apostrophes et barres doublées, hors ASCII en \X2\…\X0\. */
export function stepString(s) {
  let out = '';
  for (const ch of String(s ?? '')) {
    const c = ch.codePointAt(0);
    if (ch === "'") out += "''";
    else if (ch === '\\') out += '\\\\';
    else if (c >= 32 && c < 127) out += ch;
    else if (c < 32) out += ' ';
    else if (c <= 0xffff) out += `\\X2\\${c.toString(16).toUpperCase().padStart(4, '0')}\\X0\\`;
    else out += `\\X4\\${c.toString(16).toUpperCase().padStart(8, '0')}\\X0\\`;
  }
  return `'${out}'`;
}

function hexColor(c) {
  let s = String(c ?? '').trim().replace(/^#/, '');
  if (/^[\da-f]{3}$/i.test(s)) s = [...s].map((x) => x + x).join('');
  if (!/^[\da-f]{6}$/i.test(s)) return [0.69, 0.71, 0.73];
  return [0, 2, 4].map((k) => Math.round((parseInt(s.slice(k, k + 2), 16) / 255) * 1000) / 1000);
}

const sub3 = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const norm = (a) => { const n = Math.hypot(...a) || 1; return a.map((v) => v / n); };
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

/** Une direction perpendiculaire à n (référence d'un repère). */
function perpTo(n) {
  const a = Math.abs(n[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
  return norm(cross(cross(n, a), n).map((v) => -v));
}

/** Polygone nettoyé : points consécutifs confondus retirés. */
export function cleanLoop(pts) {
  const out = [];
  for (const p of pts) {
    const q = out[out.length - 1];
    if (!q || Math.hypot(p[0] - q[0], p[1] - q[1]) > 1e-4) out.push(p);
  }
  while (out.length > 2 && Math.hypot(out[0][0] - out[out.length - 1][0], out[0][1] - out[out.length - 1][1]) <= 1e-4) out.pop();
  return out;
}

/**
 * Rédacteur d'entités : #n = ENTITE(...). Les points et directions sont mutualisés,
 * ce qui réduit nettement la taille d'un assemblage de profilés.
 */
class Writer {
  constructor() { this.lines = []; this.pts = new Map(); this.dirs = new Map(); }
  add(body) { this.lines.push(body); return `#${this.lines.length}`; }
  point(p) {
    const k = p.map((v) => stepReal(Math.round(v * 1e6) / 1e6)).join(',');
    let id = this.pts.get(k);
    if (!id) { id = this.add(`CARTESIAN_POINT('',(${k}))`); this.pts.set(k, id); }
    return id;
  }
  dir(d) {
    const k = norm(d).map((v) => stepReal(Math.round(v * 1e12) / 1e12)).join(',');
    let id = this.dirs.get(k);
    if (!id) { id = this.add(`DIRECTION('',(${k}))`); this.dirs.set(k, id); }
    return id;
  }
  axis(o, z, x) { return this.add(`AXIS2_PLACEMENT_3D('',${this.point(o)},${this.dir(z)},${this.dir(x ?? perpTo(z))})`); }
}

/**
 * Solide d'une primitive résolue, dans le repère du composant.
 * Rend l'identifiant du MANIFOLD_SOLID_BREP.
 */
function writeSolid(w, p, name) {
  const M = p.rot ? rotMatrix(p.rot) : [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  const P = (v) => add(apply(M, v), p.pos);
  const D = (v) => apply(M, v);

  if (p.type === 'cyl') return writeCylinder(w, p, P, D, name);

  // Prisme : section (sx, sy) extrudée sur t ∈ [0, L], permutée par extrudeMap (rotation directe).
  let outer;
  let holes;
  let L;
  let axis;
  if (p.type === 'box') {
    const [x, y, z] = p.size;
    outer = [[0, 0], [x, 0], [x, y], [0, y]];
    holes = [];
    L = z;
    axis = 'z';
  } else {
    const sec = resolveSection(p.section);
    outer = cleanLoop(sec.outer);
    holes = (sec.holes ?? []).map(cleanLoop).filter((h) => h.length >= 3);
    L = p.length;
    axis = p.axis ?? 'x';
  }
  if (outer.length < 3) throw new Error('section dégénérée');
  // Contour extérieur dans le sens trigonométrique, alésages dans le sens horaire.
  if (polygonArea(outer) < 0) outer = outer.slice().reverse();
  holes = holes.map((h) => (polygonArea(h) > 0 ? h.slice().reverse() : h));
  const loops = [outer, ...holes];
  const map = (q, t) => P(extrudeMap(axis, q[0], q[1], t));
  const tdir = D(extrudeMap(axis, 0, 0, 1));

  // Sommets, puis arêtes partagées (une EDGE_CURVE par paire de sommets).
  const vtx = new Map();
  const vertex = (pt) => {
    const id = w.point(pt);
    let v = vtx.get(id);
    if (!v) { v = { id: w.add(`VERTEX_POINT('',${id})`), pt }; vtx.set(id, v); }
    return v;
  };
  const edges = new Map();
  const oriented = (a, b) => {
    const k1 = `${a.id}>${b.id}`;
    const k2 = `${b.id}>${a.id}`;
    let e = edges.get(k1);
    if (e) return `ORIENTED_EDGE('',*,*,${e},.T.)`;
    e = edges.get(k2);
    if (e) return `ORIENTED_EDGE('',*,*,${e},.F.)`;
    const d = sub3(b.pt, a.pt);
    const len = Math.hypot(...d);
    const line = w.add(`LINE('',${w.point(a.pt)},${w.add(`VECTOR('',${w.dir(d)},${stepReal(len)})`)})`);
    e = w.add(`EDGE_CURVE('',${a.id},${b.id},${line},.T.)`);
    edges.set(k1, e);
    return `ORIENTED_EDGE('',*,*,${e},.T.)`;
  };
  const loop = (vs) => w.add(`EDGE_LOOP('',(${vs.map((v, i) => w.add(oriented(v, vs[(i + 1) % vs.length]))).join(',')}))`);
  const faces = [];
  const planeFace = (boundLoops, origin, normal, ref) => {
    const bounds = boundLoops.map((vs, k) => w.add(`${k === 0 ? 'FACE_OUTER_BOUND' : 'FACE_BOUND'}('',${loop(vs)},.T.)`));
    const plane = w.add(`PLANE('',${w.axis(origin, normal, ref)})`);
    faces.push(w.add(`ADVANCED_FACE('',(${bounds.join(',')}),${plane},.T.)`));
  };

  const bottom = loops.map((lp) => lp.map((q) => vertex(map(q, 0))));
  const top = loops.map((lp) => lp.map((q) => vertex(map(q, L))));
  const o0 = map([0, 0], 0);
  const xref = D(extrudeMap(axis, 1, 0, 0));
  // Dessous (normale −t) : boucles parcourues à l'envers ; dessus (+t) : à l'endroit.
  planeFace(bottom.map((vs) => vs.slice().reverse()), o0, tdir.map((v) => -v), xref);
  planeFace(top, map([0, 0], L), tdir, xref);
  // Flancs : une face plane par côté de chaque boucle (normale = côté × t, vers l'extérieur).
  loops.forEach((lp, li) => {
    const n = lp.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const b0 = bottom[li][i]; const b1 = bottom[li][j];
      const t1 = top[li][j]; const t0 = top[li][i];
      const side = norm(sub3(b1.pt, b0.pt));
      planeFace([[b0, b1, t1, t0]], b0.pt, cross(side, tdir), side);
    }
  });
  const shell = w.add(`CLOSED_SHELL('',(${faces.join(',')}))`);
  return w.add(`MANIFOLD_SOLID_BREP(${stepString(name)},${shell})`);
}

/** Cylindre : axe t de 0 à h, rayon r ; demi-faces 0→π et π→2π. */
function writeCylinder(w, p, P, D, name) {
  const { r, h } = p;
  const axis = p.axis ?? 'z';
  const m = (u, v, t) => P(extrudeMap(axis, u, v, t));
  const T = D(extrudeMap(axis, 0, 0, 1));
  const U = D(extrudeMap(axis, 1, 0, 0));
  const c0 = m(0, 0, 0);
  const c1 = m(0, 0, h);
  const V = (pt) => w.add(`VERTEX_POINT('',${w.point(pt)})`);
  const v0 = V(m(r, 0, 0)); const v1 = V(m(-r, 0, 0));
  const v2 = V(m(r, 0, h)); const v3 = V(m(-r, 0, h));
  const circB = w.add(`CIRCLE('',${w.axis(c0, T, U)},${stepReal(r)})`);
  const circT = w.add(`CIRCLE('',${w.axis(c1, T, U)},${stepReal(r)})`);
  const edge = (a, b, curve) => w.add(`EDGE_CURVE('',${a},${b},${curve},.T.)`);
  const line = (a) => w.add(`LINE('',${w.point(a)},${w.add(`VECTOR('',${w.dir(T)},${stepReal(h)})`)})`);
  const eb1 = edge(v0, v1, circB); const eb2 = edge(v1, v0, circB);
  const et1 = edge(v2, v3, circT); const et2 = edge(v3, v2, circT);
  const l0 = edge(v0, v2, line(m(r, 0, 0)));
  const l1 = edge(v1, v3, line(m(-r, 0, 0)));
  const oe = (e, s) => w.add(`ORIENTED_EDGE('',*,*,${e},${s ? '.T.' : '.F.'})`);
  const face = (oes, surface) => {
    const lp = w.add(`EDGE_LOOP('',(${oes.join(',')}))`);
    return w.add(`ADVANCED_FACE('',(${w.add(`FACE_OUTER_BOUND('',${lp},.T.)`)}),${surface},.T.)`);
  };
  const cylSurf = w.add(`CYLINDRICAL_SURFACE('',${w.axis(c0, T, U)},${stepReal(r)})`);
  const faces = [
    face([oe(eb2, false), oe(eb1, false)], w.add(`PLANE('',${w.axis(c0, T.map((x) => -x), U)})`)),
    face([oe(et1, true), oe(et2, true)], w.add(`PLANE('',${w.axis(c1, T, U)})`)),
    face([oe(eb1, true), oe(l1, true), oe(et1, false), oe(l0, false)], cylSurf),
    face([oe(eb2, true), oe(l0, true), oe(et2, false), oe(l1, false)], cylSurf),
  ];
  const shell = w.add(`CLOSED_SHELL('',(${faces.join(',')}))`);
  return w.add(`MANIFOLD_SOLID_BREP(${stepString(name)},${shell})`);
}

/** Nom de fichier : « <nom du projet>.stp ». */
export function stepFileName(project) {
  const base = (project?.name ?? '').replace(/[\\/:*?"<>|\u0000-\u001f]+/g, '_').trim();
  return `${base || 'projet'}.stp`;
}

/**
 * Fichier STEP AP214 du projet (texte). `hidden` : ids d'instances exclues.
 * Rend { text, parts, instances } (nombre de définitions de pièces et d'occurrences).
 */
export function buildStep(project, lookup, { hidden = new Set(), author = '', now = new Date(), version = '' } = {}) {
  const resolvedOf = (i) => {
    const c = lookup(i.componentId);
    try { return c ? resolveComponent(c, i.params) : null; } catch { return null; }
  };
  const F = computeFrames(project, resolvedOf);
  const w = new Writer();

  // Contextes communs.
  const appCtx = w.add("APPLICATION_CONTEXT('core data for automotive mechanical design processes')");
  w.add(`APPLICATION_PROTOCOL_DEFINITION('international standard','automotive_design',2000,${appCtx})`);
  const prodCtx = w.add(`PRODUCT_CONTEXT('',${appCtx},'mechanical')`);
  const pdCtx = w.add(`PRODUCT_DEFINITION_CONTEXT('part definition',${appCtx},'design')`);
  const mm = w.add('( LENGTH_UNIT() NAMED_UNIT(*) SI_UNIT(.MILLI.,.METRE.) )');
  const rad = w.add('( NAMED_UNIT(*) PLANE_ANGLE_UNIT() SI_UNIT($,.RADIAN.) )');
  const sr = w.add('( NAMED_UNIT(*) SI_UNIT($,.STERADIAN.) SOLID_ANGLE_UNIT() )');
  const unc = w.add(`UNCERTAINTY_MEASURE_WITH_UNIT(LENGTH_MEASURE(1.E-04),${mm},'distance_accuracy_value','confusion accuracy')`);
  const geoCtx = w.add(`( GEOMETRIC_REPRESENTATION_CONTEXT(3) GLOBAL_UNCERTAINTY_ASSIGNED_CONTEXT((${unc})) GLOBAL_UNIT_ASSIGNED_CONTEXT((${mm},${rad},${sr})) REPRESENTATION_CONTEXT('Context #1','3D Context with UNIT and UNCERTAINTY') )`);
  const ORIGIN = [0, 0, 0];

  const usedIds = new Set();
  const productId = (base) => {
    let id = String(base).slice(0, 60) || 'piece';
    for (let k = 2; usedIds.has(id); k++) id = `${String(base).slice(0, 56)}-${k}`;
    usedIds.add(id);
    return id;
  };
  const writeRep = (n) => {
    w.lines[Number(n.rep.slice(1)) - 1] = `${n.repKind}(${stepString(n.name)},(${[...n.items, ...n.placements].join(',')}),${geoCtx})`;
  };
  /** Produit + définition + forme ; rend { pd, rep, origin } (origin = placement identité du rep). */
  const product = (id, name, repKind, items) => {
    const prod = w.add(`PRODUCT(${stepString(productId(id))},${stepString(name)},'',(${prodCtx}))`);
    w.add(`PRODUCT_RELATED_PRODUCT_CATEGORY('part',$,(${prod}))`);
    const pdf = w.add(`PRODUCT_DEFINITION_FORMATION('','',${prod})`);
    const pd = w.add(`PRODUCT_DEFINITION('design','',${pdf},${pdCtx})`);
    const pds = w.add(`PRODUCT_DEFINITION_SHAPE('','',${pd})`);
    const origin = w.axis(ORIGIN, [0, 0, 1], [1, 0, 0]);
    const node = { pd, origin, children: [], placements: [], name, repKind, items: [origin, ...items] };
    node.rep = w.add(''); // écrite par writeRep (un assemblage y ajoute les placements de ses fils)
    writeRep(node);
    w.add(`SHAPE_DEFINITION_REPRESENTATION(${pds},${node.rep})`);
    return node;
  };

  // Arborescence : sous-ensembles visibles qui contiennent au moins une pièce.
  const visible = project.instances.filter((i) => !hidden.has(i.id) && F.get(i.id) && resolvedOf(i));
  const gById = new Map((project.groups ?? []).map((g) => [g.id, g]));
  const groupOf = (gid) => (gid && gById.has(gid) ? gid : null);
  const used = new Set();
  for (const i of visible) {
    for (let g = groupOf(i.groupId), guard = 0; g && guard < 50; g = groupOf(gById.get(g).parentId), guard++) used.add(g);
  }

  const root = product(`quickplan-${project.id ?? 'projet'}`, project.name || 'QuickPlan', 'SHAPE_REPRESENTATION', []);
  const groups = new Map();
  const groupNode = (gid, guard = 0) => {
    if (!gid || !used.has(gid) || guard > 50) return root;
    let n = groups.get(gid);
    if (!n) {
      const g = gById.get(gid);
      n = product(`groupe-${g.id}`, g.name || 'Sous-ensemble', 'SHAPE_REPRESENTATION', []);
      groups.set(gid, n);
      groupNode(g.parentId, guard + 1).children.push({ node: n, name: g.name || 'Sous-ensemble', frame: null });
    }
    return n;
  };

  // Définitions de pièces partagées.
  const defs = new Map();
  const styled = [];
  const definition = (inst, comp, res) => {
    const color = inst.color ?? comp.color ?? null;
    const key = `${inst.componentId}|${JSON.stringify(res.parts)}|${color ?? ''}`;
    let d = defs.get(key);
    if (d) return d;
    const solids = [];
    res.parts.forEach((p, k) => {
      const sname = p.name || (res.parts.length > 1 ? `${comp.name} ${k + 1}` : comp.name);
      try {
        const sid = writeSolid(w, p, sname);
        solids.push(sid);
        const rgb = hexColor(inst.color ?? p.color ?? comp.color);
        const col = w.add(`COLOUR_RGB('',${rgb.map(stepReal).join(',')})`);
        const fill = w.add(`FILL_AREA_STYLE('',(${w.add(`FILL_AREA_STYLE_COLOUR('',${col})`)}))`);
        const side = w.add(`SURFACE_SIDE_STYLE('',(${w.add(`SURFACE_STYLE_FILL_AREA(${fill})`)}))`);
        const psa = w.add(`PRESENTATION_STYLE_ASSIGNMENT((${w.add(`SURFACE_STYLE_USAGE(.BOTH.,${side})`)}))`);
        styled.push(w.add(`STYLED_ITEM('color',(${psa}),${sid})`));
      } catch { /* primitive dégénérée : ignorée */ }
    });
    const dims = Object.entries(inst.params ?? {}).map(([k, v]) => `${k}=${v}`).join(' ');
    const name = `${comp.name}${dims ? ` (${dims})` : ''}`;
    d = solids.length ? product(`${inst.componentId}-${defs.size + 1}`, name, 'ADVANCED_BREP_SHAPE_REPRESENTATION', solids) : null;
    defs.set(key, d);
    return d;
  };

  let count = 0;
  for (const inst of visible) {
    const comp = lookup(inst.componentId);
    const d = definition(inst, comp, resolvedOf(inst));
    if (!d) continue;
    groupNode(inst.groupId).children.push({ node: d, name: inst.name || comp.name, frame: F.get(inst.id), id: inst.id });
    count++;
  }
  if (!count) throw new Error('Aucune pièce visible à exporter');

  // Occurrences : NAUO + placement (CDSR) du fils dans le repère du père.
  let nauo = 0;
  const link = (parent) => {
    for (const c of parent.children) {
      const f = c.frame;
      const target = f
        ? w.axis(f.t, [f.R[0][2], f.R[1][2], f.R[2][2]], [f.R[0][0], f.R[1][0], f.R[2][0]])
        : w.axis(ORIGIN, [0, 0, 1], [1, 0, 0]);
      parent.placements.push(target);
      const occ = w.add(`NEXT_ASSEMBLY_USAGE_OCCURRENCE(${stepString(`NAUO${++nauo}`)},${stepString(c.name)},'',${parent.pd},${c.node.pd},$)`);
      const pds = w.add(`PRODUCT_DEFINITION_SHAPE('','',${occ})`);
      const idt = w.add(`ITEM_DEFINED_TRANSFORMATION('','',${c.node.origin},${target})`);
      const rr = w.add(`( REPRESENTATION_RELATIONSHIP('','',${c.node.rep},${parent.rep}) REPRESENTATION_RELATIONSHIP_WITH_TRANSFORMATION(${idt}) SHAPE_REPRESENTATION_RELATIONSHIP() )`);
      w.add(`CONTEXT_DEPENDENT_SHAPE_REPRESENTATION(${rr},${pds})`);
      if (!c.frame) link(c.node);
    }
  };
  link(root);

  // Les placements cibles doivent figurer parmi les éléments de la représentation du père.
  for (const n of [root, ...groups.values()]) writeRep(n);
  const lines = w.lines;
  if (styled.length) w.add(`MECHANICAL_DESIGN_GEOMETRIC_PRESENTATION_REPRESENTATION('',(${styled.join(',')}),${geoCtx})`);

  const stamp = now.toISOString().slice(0, 19);
  const text = [
    'ISO-10303-21;',
    'HEADER;',
    `FILE_DESCRIPTION(('QuickPlan - assemblage'),'2;1');`,
    `FILE_NAME(${stepString(stepFileName(project))},'${stamp}',(${stepString(author)}),(''),${stepString(`QuickPlan ${version}`.trim())},'QuickPlan','');`,
    "FILE_SCHEMA(('AUTOMOTIVE_DESIGN { 1 0 10303 214 1 1 1 1 }'));",
    'ENDSEC;',
    'DATA;',
    ...lines.map((l, i) => `#${i + 1}=${l};`),
    'ENDSEC;',
    'END-ISO-10303-21;',
    '',
  ].join('\n');
  return { text, parts: [...defs.values()].filter(Boolean).length, instances: count };
}
